import {toTwoDecimals} from "./picos/numberFormat";
import {Err, Ok, Result} from "oxide.ts";
import sleep from "./picos/sleep";
import {HttpError} from "./picos/http";

export type FetchFileCallback = (progress: number) => void;

/**
 * Reads the body as text. A callback gets progress from a counting stream; the body is never
 * kept as an array of chunks and copied, which held each file three or four times over (OWA-1378).
 */
async function readText(response: Response, cb?: FetchFileCallback, signal?: AbortSignal): Promise<string> {
	if (typeof (cb) !== 'function') return response.text();

	const contentLength = parseInt(response.headers.get('Content-Length') || '0');
	let receivedLength = 0;
	const counter = new TransformStream<Uint8Array, Uint8Array>({
		async transform(chunk, controller) {
			signal?.throwIfAborted();
			controller.enqueue(chunk);
			receivedLength += chunk.length;
			cb(toTwoDecimals(receivedLength / contentLength));
			await sleep(1); // yield for display
		}
	});
	// Chromium reports a stream error as "Failed to fetch"; prefer the abort reason
	const text = await new Response(response.body.pipeThrough(counter)).text()
		.catch(err => {
			signal?.throwIfAborted();
			throw err;
		});
	signal?.throwIfAborted();
	cb(1);
	await sleep(1); // yield for display
	return text;
}

/**
 *
 * @param {string} url - the url to fetch
 * @param {boolean} cors - use cors in fetch?
 * @param {FetchFileCallback} [cb] - an optional callback function to pass read %
 * @returns {Promise<string|any>} - the file that was read as string or a json object/array
 */
async function fetchFileWithProgress(url: string, cors: boolean, cb?: FetchFileCallback, signal?: AbortSignal): Promise<string | any> {
	let response: Response;

	// Always a GET
	const options = {
		method: 'GET',
		mode: null,
		signal,
	}

	// Apply CORS options to the call
	options.mode = cors ? 'cors' : 'no-cors';

	// Get the file
	response = await fetch(url, options);
	signal?.throwIfAborted();

	// If we did not get a valid response, throw an error
	if (!response.ok) throw Error(`Fetch failed. ${response.status} - ${response.statusText}`);

	// If the response says that it is using the body, but there isn't a body, throw an error
	if (response.bodyUsed && !response.body) throw ('No response body from ' + url);

	const isJSON = response.headers.get('content-type')?.includes('application/json');
	const rawText = await readText(response, cb, signal);
	signal?.throwIfAborted();

	if (isJSON){
		try {
			return JSON.parse(rawText);
		}
		catch (err) {
			console.error('fetchFileWithProgress: error parsing JSON', err.message);
            return rawText;
        }
	}
	else return rawText;
}

/**
 *
 * @param {string} url - the url to fetch
 * @param {boolean} cors - use cors in fetch?
 * @param {FetchFileCallback} [cb] - an optional callback function to pass read %
 * @returns {Promise<string|any>} - the file that was read as string or a json object/array
 */
export async function fetchFileWithProgressResult(url: string, cors: boolean, cb?: FetchFileCallback): Promise<Result<string, HttpError>> {
	let response: Response;

	// Always a GET
	const options = {
		method: 'GET',
		mode: null
	}

	// Apply CORS options to the call
	options.mode = cors ? 'cors' : 'no-cors';

	// Get the file
	response = await fetch(url, options);

	// If we did not get a valid response, throw an error
	if (!response.ok) return Err(new HttpError(response.status, response.statusText || `Request failed (HTTP ${response.status})`));

	// If the response says that it is using the body, but there isn't a body, throw an error
	if (response.bodyUsed && !response.body) return Err(new HttpError(response.status, 'No response body from' + url));

	const isJSON = response.headers.get('content-type')?.includes('application/json');
	const rawText = await readText(response, cb);

	if (isJSON){
		try {
			return Ok(JSON.parse(rawText));
		}
		catch (err) {
			console.error('fetchFileWithProgress: error parsing JSON', err.message);
			return Ok(rawText);
		}
	}
	else return Ok(rawText);
}

export default fetchFileWithProgress;
